import { createSelector } from 'reselect';
import { KEYS } from '../constants';
import { selectSelectedItemChangesClone, selectNewItem, selectThisSlice } from './selectors';


/** ************
 * HELPERS
 */

const isDefined = v => v !== null && v !== undefined;

const isEmptyString = v => typeof v === 'string' && v.trim().length === 0;

const isEmptyObject = (o) => {
  if (!isDefined(o) || !(typeof o === 'object')) {
    return false;
  }

  if (Object.keys(o).length === 0) {
    return true;
  }

  const isNewObject = !o.id;
  if (isNewObject) {
    // make sure at least one property contains a non empty string or number greater than zero
    const keys = Object.keys(o);
    return !keys.some(k => {
      const val = o[k];
      return isDefined(val) && !!val && !isEmptyString(val);
    })
  }

  return false;
};

const _isEmptyProp = (o, p) => {
  const val = (o || {})[p];
  const result = !isDefined(val) || isEmptyString(val) || isEmptyObject(val, { debug: true });
  return result;
}

/** ************
 * SELECTORS
 */

export const selectShowValidataion = (state) => {
  const slice = selectThisSlice(state);
  const { showValidation } = slice;
  return showValidation;
};

// LINE
export const selectIsInvalidLineMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.LINE);
    if (isEmpty) { 
      return 'Line';
    }
    return undefined;
  }
)

// JOB_NUMBER
export const selectIsInvalidJobMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.JOB_NUMBER);
    const isAutoGenerated = !!clone[KEYS.AUTO_GENERATE_JOB_NUMBER]
    if (isEmpty && !isAutoGenerated) { 
      return 'Job';
    }
    return undefined;
  }
)

// JOB_STATUS
export const selectIsInvalidStatusMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.JOB_STATUS);
    if (isEmpty) { 
      return 'Status';
    }
    return undefined;
  }
)

// AEGION_PROJECT_MANAGER
export const selectIsInvalidAegionProjectManagerMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.AEGION_PROJECT_MANAGER);
    if (isEmpty) { 
      return 'Corrpro Project Manager';
    }
    return undefined;
  }
)

// CUSTOMER
export const selectIsInvalidCustomerMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.CUSTOMER);
    if (isEmpty) { 
      return 'Customer';
    }
    return undefined;
  }
)

// CUSTOMER_ENTITY
export const selectIsInvalidEntityMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.CUSTOMER_ENTITY);
    if (isEmpty) { 
      return 'Entity';
    }
    return undefined;
  }
)

// CUSTOMER_AREA
export const selectIsInvalidAreaMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.CUSTOMER_AREA);
    if (isEmpty) { 
      return 'Area';
    }
    return undefined;
  }
)

// CUSTOMER_PIPE
export const selectIsInvalidPipeMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.CUSTOMER_PIPE);
    if (isEmpty) { 
      return 'Pipe Id';
    }
    return undefined;
  }
)

// CUSTOMER_REG_STATUS
export const selectIsInvalidRegulatoryStatusMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.CUSTOMER_REG_STATUS);
    if (isEmpty) { 
      return 'Regulatory Status';
    }
    return undefined;
  }
)

// CUSTOMER_PROJECT_MANAGER
export const selectIsInvalidProjectManagerMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.CUSTOMER_PROJECT_MANAGER);
    if (isEmpty) { 
      return 'Project Manager';
    }
    return undefined;
  }
)

// CUSTOMER_CORROSION_TECHNICIAN
export const selectIsInvalidCorrosionTechicianMessage = createSelector(
  [
    selectSelectedItemChangesClone,
    selectNewItem,
  ],
  (item, newItem) => { 
    const clone = item || newItem;
    const isEmpty = _isEmptyProp(clone, KEYS.CUSTOMER_CORROSION_TECHNICIAN);
    if (isEmpty) { 
      return 'Corrosion Techician';
    }
    return undefined;
  }
)

// SCROLL TO INVALID PROP
export const selectScrollToInvalidProp = createSelector(
  [
    selectIsInvalidCustomerMessage,
    selectIsInvalidEntityMessage,
    selectIsInvalidAreaMessage,
    selectIsInvalidPipeMessage,
    selectIsInvalidRegulatoryStatusMessage,
    selectIsInvalidProjectManagerMessage,
    selectIsInvalidCorrosionTechicianMessage,
    selectIsInvalidLineMessage,
    selectIsInvalidJobMessage,
    selectIsInvalidStatusMessage,
    selectIsInvalidAegionProjectManagerMessage,
  ],
  (...messages) => {
    const props = [
      KEYS.CUSTOMER,
      KEYS.CUSTOMER_ENTITY,
      KEYS.CUSTOMER_AREA,
      KEYS.CUSTOMER_PIPE,
      KEYS.CUSTOMER_REG_STATUS,
      KEYS.CUSTOMER_PROJECT_MANAGER,
      KEYS.CUSTOMER_CORROSION_TECHNICIAN,
      KEYS.LINE,
      KEYS.JOB_NUMBER,
      KEYS.JOB_STATUS,
      KEYS.AEGION_PROJECT_MANAGER
    ];
    const firstFailIndex = messages.findIndex(m => typeof m === 'string' && m.trim().length > 0);
    if (firstFailIndex === -1) {
      return undefined;
    }

    return props[firstFailIndex];
  }
);

export const selectIsFormValid = (state) => !selectScrollToInvalidProp(state);




export default {
  selectIsInvalidLineMessage, 
  selectIsInvalidJobMessage, 
  selectIsInvalidStatusMessage, 
  selectIsInvalidAegionProjectManagerMessage,
  selectIsInvalidCustomerMessage,
  selectIsInvalidEntityMessage,
  selectIsInvalidAreaMessage,
  selectIsInvalidPipeMessage,
  selectIsInvalidRegulatoryStatusMessage,
  selectIsInvalidProjectManagerMessage,
  selectIsInvalidCorrosionTechicianMessage,
  selectScrollToInvalidProp,
  selectIsFormValid
}